import { IconLock } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError, errorReason } from '../lib/api';
import { RequestIdCopy } from './RequestIdCopy';

// The 403 surface: "the credential is fine; THIS CALLER MAY NOT."
//
// A 403 is an ANSWER, not a fault. The second call carries the same scopes and
// the same `X-Dz-Account` claim and is refused identically, so every affordance
// that re-asks is a lie — and until `lib/query.ts` stopped exempting only 401
// from its retry predicate, the platform made that call FOUR times for one
// denial: two from the auto-retry before anything rendered, two more from a
// Retry button the reader was invited to press.
//
// This is the query-side twin of the rule the stream views already take for a
// 404 (`isAnsweredRefusal`, `RunStreamRefusal.tsx`): an answer is reported as
// itself; a fault keeps its retry in `<ErrorState/>`.
//
// It is deliberately NOT `alert--destructive`. Being told you may not see a page
// is a fact about your permissions, not a failure of the system, and the
// destructive tint is how a reader tells "something broke" from "this is the
// answer" at a glance.
//
// It DOES keep `.error-state__body`, and that is not styling laziness: it is the
// selector `e2e/story-gate.ts` fails a Playwright story on. A story reaching this
// state means a mock answered 403 where nobody intended one, which is the same
// class of silent-empty-screen the gate was built for — so splitting the class
// off would have quietly exempted every denial from it.

/**
 * There is no `onRetry`, and its ABSENCE FROM THE TYPE is the mechanism.
 *
 * A test asserting "no Retry button renders" only fails once someone has already
 * written the regression; a prop that does not exist makes `<DeniedState
 * onRetry={…}/>` a compile error at the call site that tries. `ErrorState` is
 * one import away and takes one, so the wrong component is always available to
 * anyone who genuinely has a fault — which is exactly why this one must not be
 * able to grow the affordance quietly.
 *
 * `message` overrides the derived sentence for a view with a better one; it is
 * the only slot, and it cannot carry an action.
 */
export type DeniedStateProps = {
  readonly error?: unknown;
  /** Override the derived message. `undefined` keeps the API's own `detail`. */
  readonly message?: string | undefined;
};

function requestIdOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.requestId : undefined;
}

export function DeniedState(props: DeniedStateProps): JSX.Element {
  // The API's own `detail` first: "read:secrets is owner-only" beats the generic
  // line, and it is the sentence that says which scope to go and get. The
  // fallback is `errors.denied`, never `errors.generic` — a denial that reads as
  // a breakage sends the reader to support instead of to their org owner.
  const message = (): string => props.message ?? errorReason(props.error, t('errors.denied'));

  return (
    <div class="alert alert--icon" role="alert">
      <IconLock />
      <p class="alert__title">{message()}</p>
      <div class="alert__desc error-state__body">
        <Show when={requestIdOf(props.error)}>
          {(id) => (
            <>
              {/* Diagnosis, not recovery: quoting an id to support is the one
                  affordance a 403 keeps, because it does not re-ask. */}
              <div class="error-state__actions">
                <RequestIdCopy requestId={id()} />
              </div>
              <code class="error-state__id">{id()}</code>
            </>
          )}
        </Show>
      </div>
    </div>
  );
}
